import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { 
  Lightning, Eye, Play, Trophy, Video, Calendar, CaretDown,
  ArrowRight, Clock, GameController
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveScoring() {
  const { user } = useAuth();
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ongoingTournaments, setOngoingTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);

  const fetchLiveMatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/matches/live`);
      setLiveMatches(response.data);
    } catch (error) {
      console.error("Error fetching live matches:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOngoingTournaments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tournaments?status=ongoing`);
      setOngoingTournaments(res.data || []);
      // Auto-select the first ongoing tournament
      if (res.data?.length > 0 && !selectedTournamentId) {
        setSelectedTournamentId(res.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching tournaments:", error);
    }
  }, [selectedTournamentId]);

  const fetchUpcomingMatches = useCallback(async (tournamentId) => {
    if (!tournamentId) { setUpcomingMatches([]); return; }
    setUpcomingLoading(true);
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}/schedule`);
      // Filter to only scheduled (not yet played) matches
      const scheduled = (res.data || []).filter(m => m.status === "scheduled");
      setUpcomingMatches(scheduled);
    } catch (error) {
      console.error("Error fetching schedule:", error);
      setUpcomingMatches([]);
    } finally {
      setUpcomingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveMatches();
    fetchOngoingTournaments();
    const interval = setInterval(fetchLiveMatches, 5000);
    return () => clearInterval(interval);
  }, [fetchLiveMatches, fetchOngoingTournaments]);

  useEffect(() => {
    if (selectedTournamentId) {
      fetchUpcomingMatches(selectedTournamentId);
    }
  }, [selectedTournamentId, fetchUpcomingMatches]);

  const handleStartLive = async (matchId) => {
    try {
      await axios.post(`${API}/matches/${matchId}/start-live`, {}, { withCredentials: true });
      toast.success("Match started live!");
      fetchLiveMatches();
      fetchUpcomingMatches(selectedTournamentId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start match");
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  const selectedTournament = ongoingTournaments.find(t => t.id === selectedTournamentId);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-3" data-testid="live-page-title">
            <Lightning className="w-8 h-8 text-secondary" weight="fill" />
            Live Matches
          </h1>
          <p className="text-muted-foreground mt-1">
            Watch live scores and follow matches in progress
          </p>
        </div>
        {liveMatches.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            <span className="text-sm text-muted-foreground">{liveMatches.length} Live</span>
          </div>
        )}
      </div>

      {/* Live Matches */}
      {liveMatches.length > 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {liveMatches.map((match) => (
            <Card key={match.id} className="bg-card border-border overflow-hidden" data-testid={`live-match-${match.id}`}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                    <span className="relative flex h-2 w-2 mr-1">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                    </span>
                    LIVE
                  </Badge>
                  <span className="text-sm text-muted-foreground">{match.tournament_name}</span>
                </div>
                <span className="text-xs text-muted-foreground capitalize">{match.round?.replace('_', ' ')}</span>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between py-4">
                  <div className="flex-1 text-center">
                    <Avatar className="h-16 w-16 mx-auto border-2 border-border">
                      <AvatarImage src={match.player1_photo} />
                      <AvatarFallback className="text-lg">{getInitials(match.player1_name)}</AvatarFallback>
                    </Avatar>
                    <p className="mt-2 font-medium">{match.player1_name}</p>
                  </div>
                  <div className="px-6">
                    <div className="flex items-center gap-4">
                      <span className="text-4xl font-heading font-bold text-primary">{match.player1_score}</span>
                      <span className="text-2xl text-muted-foreground">-</span>
                      <span className="text-4xl font-heading font-bold text-primary">{match.player2_score}</span>
                    </div>
                    <p className="text-center text-xs text-muted-foreground mt-1">Best of {match.best_of || 5}</p>
                  </div>
                  <div className="flex-1 text-center">
                    <Avatar className="h-16 w-16 mx-auto border-2 border-border">
                      <AvatarImage src={match.player2_photo} />
                      <AvatarFallback className="text-lg">{getInitials(match.player2_name)}</AvatarFallback>
                    </Avatar>
                    <p className="mt-2 font-medium">{match.player2_name}</p>
                  </div>
                </div>

                {match.frames && match.frames.length > 0 && (
                  <div className="mt-4 p-3 rounded-lg bg-muted/30">
                    {(() => {
                      const currentFrame = match.frames.find(f => !f.is_complete) || match.frames[match.frames.length - 1];
                      return (
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-2">Frame {currentFrame.frame_number}</p>
                          <div className="flex items-center justify-center gap-4">
                            <span className="text-2xl font-heading font-bold">{currentFrame.player1_points}</span>
                            <span className="text-muted-foreground">-</span>
                            <span className="text-2xl font-heading font-bold">{currentFrame.player2_points}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <Link to={`/live/${match.id}`} className="flex-1">
                    <Button variant="outline" className="w-full border-border">
                      <Eye className="w-4 h-4 mr-2" />Watch Live
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </motion.div>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Lightning className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
            <h3 className="text-lg font-medium">No Live Matches</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              Select a tournament below to see upcoming fixtures you can start
            </p>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Matches from Tournament Schedule - Admin Only */}
      {user?.is_admin && ongoingTournaments.length > 0 && (
        <Card className="bg-card border-border" data-testid="upcoming-fixtures-section">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-primary" weight="fill" />
              Upcoming Fixtures
            </CardTitle>
            <Select value={selectedTournamentId} onValueChange={setSelectedTournamentId}>
              <SelectTrigger className="w-64 bg-background border-border" data-testid="tournament-selector">
                <SelectValue placeholder="Select tournament" />
              </SelectTrigger>
              <SelectContent>
                {ongoingTournaments.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {upcomingLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
              </div>
            ) : upcomingMatches.length > 0 ? (
              <div className="space-y-2">
                {upcomingMatches.map(match => (
                  <div key={match.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/10 border border-border hover:border-primary/30 transition-colors" data-testid={`upcoming-match-${match.id}`}>
                    {/* Date/Time */}
                    <div className="text-center min-w-[80px]">
                      {match.scheduled_date && (
                        <p className="text-xs font-medium text-primary">{match.scheduled_date}</p>
                      )}
                      {match.scheduled_time && (
                        <p className="text-[10px] text-muted-foreground">{match.scheduled_time}</p>
                      )}
                      {!match.scheduled_date && (
                        <Clock className="w-4 h-4 text-muted-foreground mx-auto" />
                      )}
                    </div>

                    {/* Players */}
                    <div className="flex-1 flex items-center gap-2">
                      <span className="flex-1 text-right text-sm font-medium truncate">{match.player1_name}</span>
                      <span className="text-xs text-muted-foreground px-2">vs</span>
                      <span className="flex-1 text-sm font-medium truncate">{match.player2_name}</span>
                    </div>

                    {/* Round/Group/Table */}
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] capitalize">{match.round?.replace(/_/g, ' ')}</Badge>
                      {match.group_name && (
                        <Badge variant="outline" className="text-[10px]">Grp {match.group_name}</Badge>
                      )}
                      {match.table_name && (
                        <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">{match.table_name}</Badge>
                      )}
                      {match.venue_name && (
                        <span className="text-[10px] text-muted-foreground">{match.venue_name}</span>
                      )}
                    </div>

                    {/* Start Live Button (admin only) */}
                    {user?.is_admin && (
                      <Button size="sm" className="h-8 text-xs" onClick={() => handleStartLive(match.id)} data-testid={`start-live-${match.id}`}>
                        <Play className="w-3 h-3 mr-1" weight="fill" />
                        Start Live
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <GameController className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">
                  {selectedTournament
                    ? `No upcoming scheduled matches for ${selectedTournament.name}`
                    : "Select a tournament to view upcoming fixtures"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* YouTube Live Section */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-red-500" weight="fill" />
            Live Streams
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            Live YouTube streams will appear here when tournaments are being broadcast
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
